// Logica pura dei Settings (testabile senza React).

const toMin = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + m;
};
const pad = (n) => String(n).padStart(2, "0");
export const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Giorni compressi in intervalli: [0..6] -> "Lunedì–Domenica", [0,1,2,4] -> "Lunedì–Mercoledì, Venerdì".
export const dayStr = (days, names) => {
  const s = [...new Set((days || []).map(Number))].sort((a, b) => a - b);
  const parts = [];
  let i = 0;
  while (i < s.length) {
    let j = i;
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j += 1;
    parts.push(j > i ? `${names[s[i]]}–${names[s[j]]}` : names[s[i]]);
    i = j + 1;
  }
  return parts.join(", ");
};

// Giorni senza nessuna fascia di apertura = giorni di chiusura (nessun orario inserito: nessuna chiusura).
export const closedDays = (opening) => {
  if (!opening || !opening.length) return [];
  const open = new Set(opening.flatMap((o) => o.days || []));
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => !open.has(d));
};

// Fascia di prenotazione fuori dagli orari di apertura: in uno dei suoi giorni nessuna fascia di apertura la contiene.
// Senza orari di apertura inseriti non c'è nulla da segnalare.
export const outsideOpening = (shift, opening) => {
  if (!opening || !opening.length) return false;
  const start = toMin(shift.start_time);
  const end = toMin(shift.end_time || shift.last_seating);
  return (shift.weekdays || []).some(
    (d) => !opening.some((o) => o.days.includes(d) && start >= toMin(o.from) && end <= toMin(o.to))
  );
};

// Nome della fascia come lo calcola il backend: prima delle 17:00 Pranzo, dopo Cena; due dello stesso tipo -> "Cena 19:00".
export const bandKind = (start) => (toMin(start) < 17 * 60 ? "lunch" : "dinner");
export const bandName = (shift, all, labels) => {
  if (shift.name) return shift.name;
  const kind = bandKind(shift.start_time);
  const same = (all || []).filter((x) => x.active !== false && bandKind(x.start_time) === kind);
  return same.length > 1 ? `${labels[kind]} ${shift.start_time}` : labels[kind];
};

export const sortShifts = (list) => [...list].sort((a, b) => a.start_time.localeCompare(b.start_time));

// Pausa: durata dal modulo (ore + minuti) in minuti totali.
export const pauseMinutes = (hours, minutes) => Number(hours || 0) * 60 + Number(minutes || 0);

// Fine della pausa calcolata da adesso: mai oltre la mezzanotte (come il backend).
// -> { from: "HH:MM", to: "HH:MM", endOfDay: bool } oppure null se la durata è zero.
export const pauseWindow = (now, minutes, untilEndOfDay) => {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (!untilEndOfDay && !(minutes > 0)) return null;
  const end = untilEndOfDay ? midnight : new Date(Math.min(now.getTime() + minutes * 60000, midnight.getTime()));
  const endOfDay = end.getTime() === midnight.getTime();
  return { from: hhmm(now), to: endOfDay ? "" : hhmm(end), endOfDay, until: end.toISOString() };
};

// Riapertura vista dal cliente: pausa fino a mezzanotte -> domani, altrimenti "Riprova dopo le HH:MM".
export const pauseRetry = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.getHours() === 0 && d.getMinutes() === 0 ? { tomorrow: true } : { tomorrow: false, time: hhmm(d) };
};

// Cosa vede il cliente premendo Prenota: pausa su tutti i servizi, giorno speciale chiuso per tutti, oppure niente.
// closures: giorni speciali; today: AAAA-MM-GG.
export const clientBlock = ({ pause, closures, today }) => {
  if (pause && pause.active && (!pause.shifts || pause.shifts.length === 0)) {
    return { kind: "pause", message: pause.message || "", until: pause.until, retry: pauseRetry(pause.until) };
  }
  const c = (closures || []).find((x) => !x.is_open && !x.shift && x.date_from <= today && today <= x.date_to);
  return c ? { kind: "closed", title: c.reason, message: c.message || "" } : null;
};

export const sortClosures = (list) => [...list].sort((a, b) => a.date_from.localeCompare(b.date_from));

// Date del giorno speciale: "25/12/2026" oppure "24/12/2026 – 26/12/2026".
const itDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
export const closureDates = (c) => (c.date_from === c.date_to ? itDate(c.date_from) : `${itDate(c.date_from)} – ${itDate(c.date_to)}`);

// Periodo del giorno speciale: se "dal" supera "al", "al" si allinea.
export const alignDates = (from, to) => ({ date_from: from, date_to: !to || to < from ? from : to });

// Orario leggibile (HH:MM locale) dalla data ISO.
export const untilLabel = (iso) => (iso ? hhmm(new Date(iso)) : "");

// Valore medio a coperto: "41,80" o 41.8 -> numero; vuoto/0 -> null (rimosso).
export const parseMoney = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
};
// Formato italiano: 42 -> "42", 41.5 -> "41,50".
export const fmtMoney = (n) => {
  const whole = Number.isInteger(Number(n));
  return Number(n).toLocaleString("it-IT", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
};

// Campi numerici delle Regole: interi con minimo.
export const validInt = (v, min) => /^\d+$/.test(String(v).trim()) && Number(v) >= min;
